import { BUSINESS_PROCESS_KIND } from '@internal/plugin-bcm-common';
import type { ProcessStep, SupportLink } from '@internal/plugin-bcm-common';
import { DatadogSeries, UptimeCheck } from '../datadog/api';

/**
 * Rolling up live health across the things that support a business process.
 *
 * Everything here is pure and takes a LINK LIST rather than an entity, which
 * is what lets the same functions serve three altitudes without change:
 * one matrix cell, one process, and - once spec.steps is authored - one step
 * of a process. Keep that signature.
 */

/**
 * `component:default/behemoth` -> `behemoth`.
 *
 * Here rather than in a card because four places render an entity ref as a
 * name, and a change to how one of them reads (kind-qualifying an external
 * ref, say) would otherwise have to be made in four and would be made in
 * fewer.
 */
export function nameOf(entityRef: string): string {
  return entityRef.split('/').pop() ?? entityRef;
}

/**
 * The process kind, lowercased, for comparing against a kind off the wire.
 *
 * Derived from the canonical constant rather than spelled out again, and named
 * apart from it on purpose: the two used to both be called
 * BUSINESS_PROCESS_KIND with different values - 'businessprocess' here,
 * 'BusinessProcess' in the package - in a folder that imports from both. An
 * auto-import or a tidy-up that pointed a caller at the wrong one would have
 * made `kind.toLocaleLowerCase() === BUSINESS_PROCESS_KIND` permanently false,
 * silently removing all three cards from every BusinessProcess page with no
 * error anywhere.
 */
export const BUSINESS_PROCESS_KIND_LOWERCASE =
  BUSINESS_PROCESS_KIND.toLocaleLowerCase('en-US');

/** Whether a kind off the wire is the process kind, however it was cased. */
export function isProcess(kind: string): boolean {
  return kind.toLocaleLowerCase('en-US') === BUSINESS_PROCESS_KIND_LOWERCASE;
}

/**
 * The process model comes from @internal/plugin-bcm-common - the same
 * definitions the catalog processor validates against and the editor drafts
 * into, so a shape the catalog accepts and a shape this renders cannot drift
 * apart. Re-exported rather than imported-through so the rest of this module's
 * consumers keep importing their types from one place.
 *
 * Note `step.supportedBy` is how a step is AUTHORED, but not how it is read
 * here: the backend republishes the union onto spec.supportedBy with each link
 * tagged by its step, so grouping that union on `link.step` is the only way the
 * flow and the dependency set cannot disagree.
 */
export type {
  BusinessProcessSpec,
  ProcessStep,
  StepLink,
  SupportLink,
} from '@internal/plugin-bcm-common';

/** A step with the label it is shown under, and its parent if it is a fallback. */
export type NumberedStep = {
  step: ProcessStep;
  /** `3` for a main-path step, `3b` for the first fallback covering it. */
  label: string;
  /** Id of the step this one covers, absent on the main path. */
  parent?: string;
};

export type LinkStatus = 'ok' | 'crit' | 'unknown';

/**
 * Where a link's status came from.
 *
 * `inherited` matters and is shown as such: capability modules
 * (behemoth-broker-orders and its 30-odd siblings) carry no check of their
 * own, so their status is their deployable's. That is a real signal - they
 * are deployed as part of it - but it is not the module being monitored, and
 * a reader deserves to know the difference.
 */
export type HealthOrigin = 'own' | 'inherited' | 'derived' | 'none';

export type LinkHealth = {
  link: SupportLink;
  status: LinkStatus;
  origin: HealthOrigin;
  /** Entity ref the status was actually read from. */
  from?: string;
  /**
   * For a derived status, how many of our own services were up and so served
   * as evidence. Rendered so "up" carries the weight of its inference.
   */
  witnesses?: number;
};

/** Namespace the vendor entities live in (see catalog/external-apis.yaml). */
export const EXTERNAL_NAMESPACE = 'external';

/**
 * Whether a link points outside the SAVR platform.
 *
 * Read off the namespace rather than a field on the link: every vendor
 * surface lives in `external` by the catalog's own convention, so the two
 * cannot drift the way a hand-maintained flag would.
 */
export function isExternalLink(link: SupportLink): boolean {
  return namespaceOf(link.target) === EXTERNAL_NAMESPACE;
}

/**
 * Whether a link is a human surface rather than something our code calls -
 * an ops UI someone logs into, an outsourced desk.
 *
 * Kept separate from external because the two are not the same cut: FNZ Api
 * is external and automated, CX would be internal and manual. It also fails
 * and escalates differently, and in the BIA it is often the CONTINGENCY for
 * the automated path rather than a peer of it - which is why the views keep
 * them apart rather than averaging them into one status.
 */
export function isManualLink(link: SupportLink): boolean {
  return link.path === 'manual';
}

/** `api:external/fnz-api` -> `external`; a bare `kind:name` -> `default`. */
export function namespaceOf(entityRef: string): string {
  const rest = entityRef.toLocaleLowerCase('en-US').split(':')[1] ?? '';
  return rest.includes('/') ? rest.split('/')[0] : 'default';
}

function lowerRef(entityRef: string): string {
  return entityRef.toLocaleLowerCase('en-US');
}

/** Health status of one link, inheriting through subcomponentOf when needed. */
export function linkHealth(
  link: SupportLink,
  healthSummary: Record<string, string>,
  parentOf: Record<string, string> = {},
): LinkHealth {
  // The health summary is keyed by lowercased entity ref (see HealthNameBox
  // in ../datadog/GraphHealth.tsx). Looking up the ref as authored would
  // silently miss on any entity whose name is not already lowercase.
  const target = lowerRef(link.target);
  const own = healthSummary[target];
  if (own === 'ok' || own === 'crit') {
    return { link, status: own, origin: 'own', from: link.target };
  }
  const parent = parentOf[target] ?? parentOf[link.target];
  const inherited = parent ? healthSummary[lowerRef(parent)] : undefined;
  if (inherited === 'ok' || inherited === 'crit') {
    return { link, status: inherited, origin: 'inherited', from: parent };
  }
  return { link, status: 'unknown', origin: 'none' };
}

/**
 * How many of our own services are measured up in a link list - the evidence
 * deriveInfraHealth reasons from.
 *
 * Counts DISTINCT health sources, not links. Ten of behemoth's and frontier's
 * capability modules being up is two checks that passed, not ten: the modules
 * carry no check of their own and inherit one. Reporting ten would overstate
 * how independent the evidence is, the same mistake that showing eleven
 * identical availability percentages as eleven measurements would be.
 *
 * Counted separately from the derivation because the evidence belongs to the
 * PROCESS while the derivation happens per rendered group. AWS sits in the
 * unplaced block on the pilot, and a matrix cell holds one column's links, so
 * a group deriving only from its own members would find no witness in exactly
 * the places AWS appears. Whether AWS is serving us is a process-wide fact,
 * so callers rolling up a subset compute this over the full list and pass it
 * down.
 */
export function platformWitnesses(health: LinkHealth[]): number {
  const sources = new Set(
    health
      .filter(
        item =>
          item.status === 'ok' &&
          !isExternalLink(item.link) &&
          !isManualLink(item.link),
      )
      .map(item => lowerRef(item.from ?? item.link.target)),
  );
  return sources.size;
}

/**
 * The annotation by which an entity asks for its status to be inferred from
 * the platform links beside it, rather than measured.
 */
export const DERIVED_STATUS_ANNOTATION = 'savr.com/status-derived-from';

/**
 * Infer the status of infrastructure everything runs on top of.
 *
 * AWS is the case this exists for. It cannot be pinged into a useful answer -
 * a 200 from an AWS endpoint says nothing about whether OUR account is being
 * served - so it sat as a permanent hollow ring on six processes while being
 * the single dependency underneath all of them. But our services run in it,
 * so a service of ours passing its own check IS evidence: the path through
 * AWS to that task worked, end to end, within the last minute.
 *
 * The inference runs one way only, and the asymmetry is the whole design:
 *
 * - Any of our own services up  -> AWS is serving us. One witness suffices;
 *   it cannot be up by accident.
 * - None up                     -> UNKNOWN, never down. Our services failing
 *   is usually our own code, and naming AWS as the cause of our own outage
 *   would be a false accusation in a document a regulator may read.
 *
 * Two limits worth stating rather than discovering. "AWS is operational" is
 * not really a boolean: a partial outage - Kinesis down, ECS fine - leaves
 * this green while a process built on Kinesis breaks, so what is derived is
 * that our footprint is reachable, not that AWS is healthy. And this is
 * evidence about AWS from things that depend on it, so it can only ever
 * corroborate; only a check on the account itself could contradict.
 *
 * Deliberately roll-up-neutral. A derived status is only ever `ok`, and only
 * when a sibling is already `ok`, so it can never change a worst-of verdict -
 * it cannot mask a red process, and it cannot invent one.
 *
 * Never overrides a real measurement: only an `unknown` link is derived, so
 * configuring an actual check on AWS one day silently takes precedence.
 */
export function deriveInfraHealth(
  health: LinkHealth[],
  derivedFrom: Record<string, string> = {},
  witnesses = platformWitnesses(health),
): LinkHealth[] {
  return health.map(item => {
    const asks = derivedFrom[lowerRef(item.link.target)] === 'platform';
    if (!asks || item.status !== 'unknown' || witnesses === 0) {
      return item;
    }
    return { ...item, status: 'ok', origin: 'derived', witnesses };
  });
}

export type Rollup = {
  status: LinkStatus;
  /** Links with a status (own or inherited). */
  checked: number;
  total: number;
  health: LinkHealth[];
};

/**
 * Worst-of across a link list, with coverage.
 *
 * An unchecked link is `unknown` and counts against coverage - it is NOT
 * treated as healthy. That is the whole point of reporting `checked / total`
 * next to the status: a green light over two measured links out of nine is a
 * different claim from a green light over nine.
 */
export function rollUpStatus(
  links: SupportLink[],
  healthSummary: Record<string, string>,
  parentOf: Record<string, string> = {},
  derivedFrom: Record<string, string> = {},
  witnesses?: number,
): Rollup {
  // Two passes, because a derived status reads the links beside it: measure
  // every link first, then let infrastructure infer from what was measured.
  // `witnesses` is for callers rolling up a SUBSET of a process's links - see
  // platformWitnesses. Omitted, the evidence is this list's own.
  const measured = links.map(link => linkHealth(link, healthSummary, parentOf));
  const health = deriveInfraHealth(measured, derivedFrom, witnesses);
  const checked = health.filter(item => item.status !== 'unknown').length;
  let status: LinkStatus = 'unknown';
  if (health.some(item => item.status === 'crit')) {
    status = 'crit';
  } else if (checked > 0) {
    status = 'ok';
  }
  return { status, checked, total: links.length, health };
}

/**
 * The Datadog tag the health publisher writes for an entity. Must stay in
 * step with the `entity:` tag in packages/backend/src/plugins/datadogGraphs.ts
 * - the kind/name tag there drops the namespace and so cannot address
 * `api:external/fnz-api` unambiguously.
 */
export function healthTagFor(entityRef: string): string {
  return `entity:${sanitizeTagValue(entityRef)}`;
}

/** Mirror of sanitizeTagValue in packages/backend/src/plugins/savrAnalytics.ts. */
export function sanitizeTagValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_\-./:]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}

/** The `savr.backstage.health.up` query backing a link's outage measurement. */
export function healthUpQuery(entityRef: string): string {
  return `min:savr.backstage.health.up{${healthTagFor(entityRef)}}`;
}

export type OutageMeasurement = {
  /** Longest continuous run of "down" points, in seconds. */
  longestSeconds: number;
  /** Spacing between datapoints - the finest outage this can resolve. */
  resolutionSeconds: number;
  /** True when there were no usable datapoints at all. */
  empty: boolean;
};

/**
 * Longest continuous outage in a `savr.backstage.health.up` series.
 *
 * Datadog rolls long windows up coarsely, so the answer is only ever accurate
 * to `resolutionSeconds` - which is why that is returned rather than left for
 * the caller to assume. A null datapoint means "not being checked", not
 * "down": it does not count as outage and it BREAKS the run, so two separate
 * outages either side of a data gap are never merged into one long one. The
 * bias is therefore towards under-reporting across gaps; say so wherever the
 * number is shown.
 */
export function longestOutage(series: DatadogSeries[]): OutageMeasurement {
  const points = series
    .flatMap(one => one.pointlist ?? [])
    .filter(point => Array.isArray(point) && point.length >= 2)
    .sort((a, b) => a[0] - b[0]);
  if (points.length === 0) {
    return { longestSeconds: 0, resolutionSeconds: 0, empty: true };
  }
  const resolutionSeconds = medianInterval(points.map(point => point[0]));
  let longest = 0;
  let run = 0;
  for (const [, value] of points) {
    if (value === null || value === undefined) {
      run = 0;
      continue;
    }
    if (value < 1) {
      run += 1;
      longest = Math.max(longest, run);
    } else {
      run = 0;
    }
  }
  return {
    longestSeconds: longest * resolutionSeconds,
    resolutionSeconds,
    empty: false,
  };
}

/** Median gap between timestamps, in seconds. Robust to a few large gaps. */
function medianInterval(timestampsMs: number[]): number {
  if (timestampsMs.length < 2) {
    return 0;
  }
  const gaps = timestampsMs
    .slice(1)
    .map((value, index) => (value - timestampsMs[index]) / 1000)
    .filter(gap => gap > 0)
    .sort((a, b) => a - b);
  if (gaps.length === 0) {
    return 0;
  }
  return gaps[Math.floor(gaps.length / 2)];
}

export type Verdict = 'ok' | 'breach' | 'not-measured';

export type MtpdVerdict = {
  verdict: Verdict;
  /**
   * True when the measurement is coarser than the target, so a single "down"
   * datapoint already exceeds the MTPD. The breach is then possible rather
   * than established - the sheet's 2-minute targets all land here. Render it
   * as such instead of quoting a duration that implies minute accuracy.
   */
  coarse: boolean;
  longestSeconds?: number;
  resolutionSeconds?: number;
};

/** Whether a measured outage breached a declared MTPD. */
export function mtpdVerdict(
  measurement: OutageMeasurement | undefined,
  mtpdSeconds: number | undefined,
): MtpdVerdict {
  if (!measurement || measurement.empty || mtpdSeconds === undefined) {
    return { verdict: 'not-measured', coarse: false };
  }
  const { longestSeconds, resolutionSeconds } = measurement;
  return {
    verdict: longestSeconds > mtpdSeconds ? 'breach' : 'ok',
    coarse: resolutionSeconds > mtpdSeconds,
    longestSeconds,
    resolutionSeconds,
  };
}

/**
 * One query for every entity's health history.
 *
 * Deliberately unfiltered and grouped rather than one query per link: a
 * process has up to nine dependencies and the matrix has eight rows over six
 * columns, so per-link queries would mean dozens of proxy round-trips to say
 * what a single grouped series set already says.
 *
 * Grouped by BOTH tags on purpose. `entity:` is the unambiguous key but it was
 * only introduced alongside this feature, so every datapoint written before
 * then carries just the legacy `service:<name>`. Grouping by `entity` alone
 * would therefore see no history at all until the new tag had been publishing
 * for the full window - the MTPD column would read "not measured" everywhere
 * for its first 30 days. Datadog datapoints are immutable, so the history
 * cannot be re-tagged; it has to be read around.
 */
export const ALL_HEALTH_UP_QUERY =
  'min:savr.backstage.health.up{*} by {entity,service}';

/** How far back outage is measured. Matches the 30-day ping_history window. */
export const OUTAGE_WINDOW_DAYS = 30;

/**
 * The legacy tag the health publisher wrote before `entity:` existed.
 *
 * Mirrors packages/backend/src/plugins/datadogGraphs.ts: components use
 * Datadog's reserved `service`, other kinds tag by their own kind, and the
 * namespace is dropped in both cases - which is exactly why it is ambiguous
 * and why `entity:` was added. Used only as a fallback for history that
 * predates that tag.
 */
export function legacyHealthTagKeyFor(entityRef: string): string {
  const [kind, rest] = entityRef.toLocaleLowerCase('en-US').split(':');
  const name = (rest ?? '').split('/').pop() ?? '';
  return `${kind === 'component' ? 'service' : kind}:${sanitizeTagValue(name)}`;
}

/**
 * Keys a series can be found under: its entity ref when the new tag is
 * present, and its legacy `service:<name>` key when that tag is in scope.
 *
 * A series is indexed under BOTH when both are present, so old and new points
 * for one entity can be unioned rather than one shadowing the other.
 */
export function seriesKeysFromScope(scope: string | undefined): string[] {
  if (!scope) {
    return [];
  }
  const keys: string[] = [];
  for (const part of scope.split(',')) {
    const trimmed = part.trim().toLocaleLowerCase('en-US');
    if (trimmed.startsWith('entity:')) {
      const ref = trimmed.slice('entity:'.length);
      // Datadog renders a missing group tag as `n/a`; a real ref always
      // carries the kind separator.
      if (ref.includes(':')) {
        keys.push(ref);
      }
    } else if (trimmed.startsWith('service:')) {
      keys.push(trimmed);
    }
  }
  return keys;
}

/** `entity:api:external/fnz-api,check:ping` -> `api:external/fnz-api`. */
export function entityRefFromScope(
  scope: string | undefined,
): string | undefined {
  return seriesKeysFromScope(scope).find(key => !key.startsWith('service:'));
}

/**
 * Health series indexed by every key they can be looked up under - entity ref
 * and legacy tag - so a link can union both.
 */
export function indexHealthSeries(
  series: DatadogSeries[],
): Record<string, DatadogSeries[]> {
  const index: Record<string, DatadogSeries[]> = {};
  for (const one of series) {
    for (const key of seriesKeysFromScope(one.scope)) {
      (index[key] ??= []).push(one);
    }
  }
  return index;
}

/**
 * Outage for one link, following the same inheritance as linkHealth.
 *
 * Unions the entity-keyed and legacy-keyed series before measuring. The union
 * is the point: grouping by two tags splits a component's history in two -
 * pre-change points under the service key, later ones under both - so reading
 * only the entity key would silently return post-change history alone. A
 * working-looking feature reporting a wrong number is worse than a blank
 * column.
 *
 * The fallback reaches components only, since the query groups by `service`
 * and an `api:`/`resource:` tag never appears in scope. That is the right cut:
 * health checks today are ping URLs on deployables, so components are where
 * history exists, and the vendor entities have no checks configured under
 * either tag.
 */
export function outageForLink(
  health: LinkHealth,
  index: Record<string, DatadogSeries[]>,
): OutageMeasurement | undefined {
  const source = lowerRef(health.from ?? health.link.target);
  const series = [
    ...(index[source] ?? []),
    ...(index[legacyHealthTagKeyFor(source)] ?? []),
  ];
  const unique = [...new Set(series)];
  return unique.length > 0 ? longestOutage(unique) : undefined;
}

/**
 * Matrix columns, derived from the links rather than maintained by hand -
 * so adding a link (or, later, a step) never means editing a column list
 * somewhere else that then drifts.
 *
 * Ordered by how many rows depend on each resource, most-shared first: that
 * puts the columns with the widest blast radius where the eye lands, and it
 * is the question the matrix exists to answer. Ties break alphabetically so
 * the order is stable across renders.
 *
 * `columnOf` decides which column a link belongs to, and returning undefined
 * drops it from the view entirely - which is how the internal and external
 * views of /processes are built from the same links.
 */
export function deriveColumns(
  linksByRow: SupportLink[][],
  columnOf: (link: SupportLink) => string | undefined = link =>
    link.biaResource,
): string[] {
  const rowsUsing = new Map<string, number>();
  for (const links of linksByRow) {
    const columns = links
      .map(columnOf)
      .filter((column): column is string => Boolean(column));
    for (const resource of new Set(columns)) {
      rowsUsing.set(resource, (rowsUsing.get(resource) ?? 0) + 1);
    }
  }
  return [...rowsUsing.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([resource]) => resource);
}

/** Human duration for a target: "2 min", "4 h", "3 days", "none". */
/**
 * Step labels for the flow: main path 1..n in authored order, and each
 * fallback lettered against the step it covers - 3b, 3c.
 *
 * Lives here rather than in a card because the Flow card and the Support
 * card both number steps and must not drift. Parents are resolved by id, not
 * position, so a fallback authored before the step it covers still numbers
 * correctly.
 *
 * A fallback whose parent is ABSENT has nothing to hang off, so it is numbered
 * on the main path rather than dropped. The backend rejects that shape, but
 * this is a pure function over whatever it is handed, and silently vanishing
 * is the worse failure.
 *
 * One shape it does NOT number, and callers have to handle: a fallback whose
 * parent is PRESENT but is itself a fallback. Fallbacks are only iterated
 * inside the loop over main-path steps, so such a step is emitted by neither
 * branch. The backend rejects it too, so it only exists in a draft someone is
 * midway through fixing - which is exactly when losing it is worst.
 * ProcessEditor gives those steps a card of their own rather than letting them
 * disappear; anything else numbering steps has to do the same.
 *
 * Returned in reading order: each main step, then its fallbacks.
 */
export function numberSteps(steps: ProcessStep[]): NumberedStep[] {
  const ids = new Set(steps.map(step => step.id));
  const main = steps.filter(
    step => !step.fallbackFor || !ids.has(step.fallbackFor),
  );
  const numberOf = new Map(main.map((step, index) => [step.id, index + 1]));
  const out: NumberedStep[] = [];
  for (const step of main) {
    out.push({ step, label: String(numberOf.get(step.id)) });
    // 'b' onwards: the parent is implicitly the 'a', so a lone fallback on
    // step 3 reads as 3b rather than 3a.
    let suffix = 'b';
    for (const fallback of steps) {
      if (fallback.fallbackFor !== step.id) {
        continue;
      }
      out.push({
        step: fallback,
        label: `${numberOf.get(step.id)}${suffix}`,
        parent: step.id,
      });
      suffix = String.fromCharCode(suffix.charCodeAt(0) + 1);
    }
  }
  return out;
}

/**
 * The entity whose uptime history covers this link.
 *
 * A capability module never has history of its own: ping_history is keyed by
 * the DEPLOYABLE's ref, so behemoth-swish has no rows and behemoth has them
 * all. Resolving through subcomponentOf is therefore not a nicety, it is the
 * only way a module gets a number at all.
 *
 * Deliberately structural rather than reading LinkHealth.from the way
 * outageForLink does: `from` is only set when the parent appears in the
 * CURRENT health summary, so a parent momentarily absent from it would drop
 * 30 days of history that exists. Whether a module has its own series does
 * not change minute to minute.
 */
export function availabilitySourceOf(
  link: SupportLink,
  parentOf: Record<string, string> = {},
): string {
  const target = lowerRef(link.target);
  return parentOf[target] ?? parentOf[link.target] ?? link.target;
}

/**
 * The entities to fetch uptime for, given a link list: the distinct sources,
 * minus any with no check configured, sorted so the set has a stable identity.
 *
 * The uptime API is one entity per request, so this is what keeps a process
 * page cheap. Two filters do the work. Resolving through the deployable
 * collapses Withdrawal and deposit's ten components to behemoth and frontier;
 * dropping the unchecked removes its five vendor entities, none of which can
 * return a row - ping_history is only ever written for an entity with a
 * check, so absence from the health summary is a proof of emptiness rather
 * than a guess at it.
 *
 * Sorted, not merely deduped: callers use the joined list as an effect key,
 * and an unstable order would refire the fetch on every render.
 */
export function availabilitySourcesOf(
  links: SupportLink[],
  parentOf: Record<string, string> = {},
  healthSummary: Record<string, string> = {},
): string[] {
  return [...new Set(links.map(link => availabilitySourceOf(link, parentOf)))]
    .filter(source => healthSummary[lowerRef(source)] !== undefined)
    .sort();
}

/**
 * Worst 30-day availability across an entity's checks, or null when nothing
 * was measured.
 *
 * Worst rather than an average or a weighted total, to agree with how current
 * status already works: that is a `min` across checks, so one failing check
 * shows. A check with no samples reports null and is skipped - null means "not
 * measured", which is not the same claim as 0%.
 */
export function worstAvailability(checks: UptimeCheck[]): number | null {
  const measured = checks
    .map(check => check.availability?.month)
    .filter((value): value is number => typeof value === 'number');
  return measured.length > 0 ? Math.min(...measured) : null;
}

export function formatDuration(seconds: number | undefined): string {
  if (seconds === undefined) {
    return '-';
  }
  // The BIA writes Order payment's target as "0 min", and this view is read
  // beside it. "none" would read as "no MTPD defined" rather than "no
  // downtime tolerated" - the opposite of what it means.
  if (seconds === 0) {
    return '0 min';
  }
  // No weeks unit on purpose: the BIA says "14 days", and this view is read
  // alongside it (and handed to auditors), so it must not silently
  // re-express the sheet's own targets as "2 weeks".
  const units: [number, string, string][] = [
    [86400, 'day', 'days'],
    [3600, 'h', 'h'],
    [60, 'min', 'min'],
    [1, 's', 's'],
  ];
  for (const [size, singular, plural] of units) {
    if (seconds >= size && seconds % size === 0) {
      const count = seconds / size;
      return `${count} ${count === 1 ? singular : plural}`;
    }
  }
  return `${seconds} s`;
}
